import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CONFIG } from '../config/config';

/* ─── Auth ─── */
function getToken() {
  return sessionStorage.getItem('token') || localStorage.getItem('token') || '';
}

/* ─── Constantes ─── */
const ACTION_LABELS = {
  creation_manuelle:   'Création manuelle',
  creation_pdf:        'Import PDF',
  creation_apicrypt:   'Import APICRYPT',
  step_change:         'Changement étape',
  sortie:              'Sortie',
  archive:             'Archivage',
  cotation_finalized:  'Cotation finalisée',
  cotation_resync:     'Resync cotation',
  commentaire_medecin: 'Commentaire médecin',
  affectation_salle:   'Affectation salle',
  suppression:         'Suppression',
};
const ACTION_COLORS = {
  creation_manuelle:   'bg-blue-100 text-blue-700',
  creation_pdf:        'bg-indigo-100 text-indigo-700',
  creation_apicrypt:   'bg-violet-100 text-violet-700',
  step_change:         'bg-amber-100 text-amber-700',
  sortie:              'bg-green-100 text-green-700',
  archive:             'bg-slate-200 text-slate-600',
  cotation_finalized:  'bg-emerald-100 text-emerald-700',
  cotation_resync:     'bg-teal-100 text-teal-700',
  commentaire_medecin: 'bg-orange-100 text-orange-700',
  affectation_salle:   'bg-cyan-100 text-cyan-700',
  suppression:         'bg-red-100 text-red-700',
};
const ROLE_LABELS = {
  secretaire:  'Secrétaire',
  infirmiere:  'Infirmière',
  infirmier:   'Infirmier',
  medecin:     'Médecin',
  admin:       'Admin',
};
const STEP_LABELS = {
  0:'Enregistré', 1:'Attente infirmière', 2:'Infirmière', 3:'Attente médecin',
  4:'Médecin', 5:'Attente inf. 2', 6:'Infirmière 2', 7:'En sortie', 8:'Finalisé', 999:'Archivé',
};

/* ══════════════════════════════════════════════
   Composant MultiSelect avec recherche
   options : [{ id, label }]   (ou string pour actions/roles)
   value   : string[]          (ids sélectionnés)
   onChange: (string[]) => void
   placeholder: string
═══════════════════════════════════════════════ */
function MultiSelect({ options, value, onChange, placeholder }) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    function outside(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', outside);
    return () => document.removeEventListener('mousedown', outside);
  }, []);

  const filtered = options.filter(o => {
    const lbl = typeof o === 'string' ? o : o.label;
    return lbl.toLowerCase().includes(search.toLowerCase());
  });

  function toggle(id) {
    onChange(value.includes(id) ? value.filter(v => v !== id) : [...value, id]);
  }
  function clear(e) { e.stopPropagation(); onChange([]); setSearch(''); }

  const selectedLabels = value.map(v => {
    const opt = options.find(o => (typeof o === 'string' ? o : o.id) === v);
    return opt ? (typeof opt === 'string' ? opt : opt.label) : v;
  });

  return (
    <div ref={ref} className="relative">
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center justify-between gap-1 rounded-lg border px-3 py-2 text-sm text-left transition-colors
          ${open ? 'border-red-400 ring-2 ring-red-200' : 'border-slate-300 hover:border-slate-400'}
          bg-white`}
      >
        <span className="truncate text-slate-700 min-w-0">
          {value.length === 0
            ? <span className="text-slate-400">{placeholder}</span>
            : value.length === 1
              ? selectedLabels[0]
              : <span>{selectedLabels[0]}<span className="ml-1 rounded-full bg-red-100 text-red-600 text-xs px-1.5">+{value.length - 1}</span></span>
          }
        </span>
        <span className="flex items-center gap-1 shrink-0">
          {value.length > 0 && (
            <span onClick={clear} className="text-slate-400 hover:text-slate-600 text-xs px-0.5">✕</span>
          )}
          <span className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}>▾</span>
        </span>
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 mt-1 w-full min-w-[180px] rounded-lg border border-slate-200 bg-white shadow-lg">
          {/* Search */}
          <div className="p-2 border-b border-slate-100">
            <input
              autoFocus
              type="text"
              placeholder="Rechercher…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full rounded border border-slate-200 px-2 py-1 text-sm focus:outline-none focus:border-red-300"
            />
          </div>
          {/* Options */}
          <div className="max-h-48 overflow-y-auto py-1">
            {filtered.length === 0 && (
              <div className="px-3 py-2 text-xs text-slate-400">Aucun résultat</div>
            )}
            {filtered.map(opt => {
              const id  = typeof opt === 'string' ? opt : opt.id;
              const lbl = typeof opt === 'string' ? opt : opt.label;
              const sel = value.includes(id);
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => toggle(id)}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left hover:bg-slate-50 transition-colors
                    ${sel ? 'font-medium text-red-700' : 'text-slate-700'}`}
                >
                  <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0
                    ${sel ? 'bg-red-600 border-red-600 text-white' : 'border-slate-300'}`}>
                    {sel && '✓'}
                  </span>
                  {lbl}
                </button>
              );
            })}
          </div>
          {/* Tout sélectionner / désélectionner */}
          {options.length > 1 && (
            <div className="border-t border-slate-100 px-3 py-1.5 flex gap-3">
              <button type="button" onClick={() => onChange(options.map(o => typeof o==='string'?o:o.id))}
                className="text-xs text-slate-500 hover:text-red-600">Tout sélectionner</button>
              <button type="button" onClick={() => onChange([])}
                className="text-xs text-slate-500 hover:text-red-600">Tout désélectionner</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Helpers affichage ─── */
function Badge({ action }) {
  const cls = ACTION_COLORS[action] || 'bg-slate-100 text-slate-600';
  return <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>{ACTION_LABELS[action] || action}</span>;
}
function RoleBadge({ role }) {
  const colors = { secretaire:'bg-slate-100 text-slate-600', infirmiere:'bg-blue-100 text-blue-600',
    infirmier:'bg-blue-100 text-blue-600', medecin:'bg-purple-100 text-purple-700', admin:'bg-red-100 text-red-700' };
  return <span className={`inline-block rounded px-1.5 py-0.5 text-xs ${colors[role]||'bg-slate-100 text-slate-500'}`}>{ROLE_LABELS[role]||role}</span>;
}
function formatTs(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('fr-FR', { timeZone:'Europe/Paris', day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit', second:'2-digit' });
}
function DetailCell({ details, action }) {
  if (!details || !Object.keys(details).length) return <span className="text-slate-400">—</span>;
  const parts = [];
  if (['step_change','sortie','archive'].includes(action)) {
    const from = details.from != null ? (STEP_LABELS[details.from]||`Étape ${details.from}`) : null;
    const to   = details.to   != null ? (STEP_LABELS[details.to]  ||`Étape ${details.to}`)   : null;
    if (from && to) parts.push(`${from} → ${to}`);
  } else if (['cotation_finalized','cotation_resync'].includes(action)) {
    const dom = details.domaine || details.domain;
    if (details.actes?.length) {
      // Résultat Google Sheets : afficher les actes directement
      details.actes.forEach((a, i) => {
        parts.push(
          <div key={i} className="flex gap-2 text-xs items-baseline">
            <span className="font-mono font-semibold text-slate-700">{a.code}</span>
            {a.montant && <span className="text-green-700 font-medium">{a.montant} €</span>}
            {a.texte && <span className="text-slate-500 truncate max-w-xs" title={a.texte}>{a.texte}</span>}
          </div>
        );
      });
    } else {
      // Ancienne entrée sans actes : afficher le résumé codes/domaine/total
      if (details.codes) parts.push(<span key="c" className="font-mono text-xs bg-slate-100 px-1 rounded">{details.codes}</span>);
      if (dom) parts.push(<span key="d" className="text-xs text-slate-400 italic">{dom}</span>);
      if (details.total != null) parts.push(`${Number(details.total).toFixed(2)} €`);
    }
  } else if (action === 'affectation_salle') {
    if (details.salle) parts.push(`Salle ${details.salle}`);
  } else if (['creation_pdf','creation_manuelle','creation_apicrypt'].includes(action)) {
    if (details.fichier) parts.push(<em key="f" className="text-xs text-slate-400">{details.fichier}</em>);
  }
  if (!parts.length) return <span className="font-mono text-xs text-slate-400">{JSON.stringify(details).slice(0,80)}</span>;
  return <div className="flex flex-wrap gap-1.5 items-center">{parts.map((p,i)=>typeof p==='string'?<span key={i}>{p}</span>:p)}</div>;
}
function exportCsv(rows) {
  const h = ['Horodatage','Profil','Rôle','Site','Action','Patient','Détails'];
  const body = rows.map(r=>[
    formatTs(r.ts), r.profile_display||'—', ROLE_LABELS[r.user_role]||r.user_role||'—',
    r.etablissement_libelle||'—', ACTION_LABELS[r.action]||r.action,
    [r.passage_prenom,r.passage_nom].filter(Boolean).join(' ')||r.passage_uid||'—',
    JSON.stringify(r.details||{}),
  ].map(v=>`"${String(v).replace(/"/g,'""')}"`).join(';'));
  const a = Object.assign(document.createElement('a'),{
    href:URL.createObjectURL(new Blob(['\uFEFF'+[h.join(';'),...body].join('\n')],{type:'text/csv;charset=utf-8'})),
    download:`audit_${new Date().toISOString().slice(0,10)}.csv`,
  }); a.click();
}

/* ══════════════════════════════════════════════
   Page principale
═══════════════════════════════════════════════ */
export default function AdminAudit() {
  const navigate = useNavigate();

  /* Filtres */
  const [selProfiles,  setSelProfiles]  = useState([]);
  const [selActions,   setSelActions]   = useState([]);
  const [selRoles,     setSelRoles]     = useState([]);
  const [selSites,     setSelSites]     = useState([]);
  const [passageUid,   setPassageUid]   = useState('');
  const [dateFrom,     setDateFrom]     = useState('');
  const [dateTo,       setDateTo]       = useState('');

  /* Méta (options des selects) */
  const [metaProfiles,  setMetaProfiles]  = useState([]);
  const [metaSites,     setMetaSites]     = useState([]);
  const [metaRoles,     setMetaRoles]     = useState([]);
  const ACTION_OPTIONS = Object.entries(ACTION_LABELS).map(([id, label]) => ({ id, label }));

  /* Profils filtrés selon les rôles sélectionnés, dédoublonnés par id */
  const filteredProfiles = useMemo(() => {
    const base = selRoles.length
      ? metaProfiles.filter(p => selRoles.includes(p.role))
      : metaProfiles;
    const seen = new Set();
    return base.filter(p => {
      if (seen.has(p.id)) return false;
      seen.add(p.id);
      return true;
    });
  }, [selRoles, metaProfiles]);

  /* Résultats */
  const [rows,    setRows]    = useState([]);
  const [total,   setTotal]   = useState(0);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [offset,  setOffset]  = useState(0);
  const [expanded,setExpanded]= useState(null);
  const LIMIT = 100;
  const debRef = useRef(null);

  /* Charger les méta au montage */
  useEffect(() => {
    fetch(`${CONFIG.API_URL}/audit/meta`, { headers:{ Authorization:`Bearer ${getToken()}` } })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(d => {
        setMetaProfiles(d.profiles  || []);
        setMetaSites(d.etablissements || []);
        setMetaRoles((d.roles || []).map(r => ({ id: r, label: ROLE_LABELS[r] || r })));
      })
      .catch(e => console.warn('audit meta:', e));
  }, []);

  const fetchAudit = useCallback(async (off = 0) => {
    setLoading(true); setError('');
    try {
      const p = new URLSearchParams();
      if (selProfiles.length)  p.set('profile', selProfiles.join(','));
      if (selActions.length)   p.set('action',       selActions.join(','));
      if (selRoles.length)     p.set('role',          selRoles.join(','));
      if (selSites.length)     p.set('etablissement', selSites.join(','));
      if (passageUid.trim())   p.set('passage_uid',   passageUid.trim());
      if (dateFrom)            p.set('date_from', dateFrom);
      if (dateTo)              p.set('date_to',   dateTo);
      p.set('limit', String(LIMIT)); p.set('offset', String(off));

      const r = await fetch(`${CONFIG.API_URL}/audit?${p}`, { headers:{ Authorization:`Bearer ${getToken()}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setRows(d.rows||[]); setTotal(d.total||0); setOffset(off);
    } catch(e) { setError(e.message); }
    finally    { setLoading(false); }
  }, [selProfiles, selActions, selRoles, selSites, passageUid, dateFrom, dateTo, metaProfiles]);

  useEffect(() => {
    clearTimeout(debRef.current);
    debRef.current = setTimeout(() => fetchAudit(0), 350);
    return () => clearTimeout(debRef.current);
  }, [fetchAudit]);

  function reset() {
    setSelProfiles([]); setSelActions([]); setSelRoles([]); setSelSites([]);
    setPassageUid(''); setDateFrom(''); setDateTo('');
  }
  const hasFilters = selProfiles.length||selActions.length||selRoles.length||selSites.length||passageUid||dateFrom||dateTo;

  const INPUT = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-300";

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button onClick={()=>navigate(-1)} className="text-slate-500 hover:text-slate-700 text-sm">← Retour</button>
            <h1 className="text-lg font-semibold text-slate-800">Journal d'activité</h1>
            {total > 0 && <span className="text-xs text-slate-500 tabular-nums">{total.toLocaleString('fr-FR')} entrée{total>1?'s':''}</span>}
          </div>
          <button onClick={()=>exportCsv(rows)} disabled={!rows.length}
            className="px-3 py-1.5 text-sm rounded border border-slate-300 hover:bg-slate-50 disabled:opacity-40">
            ↓ Export CSV
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-4 space-y-4">

        {/* ── Filtres ── */}
        <div className="bg-white rounded-xl border p-4 space-y-3">
          {/* Ligne 1 : 4 multiselects */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Profil</label>
              <MultiSelect
                options={filteredProfiles}
                value={selProfiles}
                onChange={v => {
                  // Garder uniquement les profils qui sont dans filteredProfiles
                  setSelProfiles(v.filter(id => filteredProfiles.some(p => p.id === id)));
                }}
                placeholder="Tous les profils"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Site</label>
              <MultiSelect
                options={metaSites}
                value={selSites}
                onChange={setSelSites}
                placeholder="Tous les sites"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Rôle</label>
              <MultiSelect
                options={metaRoles}
                value={selRoles}
                onChange={newRoles => {
                  setSelRoles(newRoles);
                  // Purger les profils sélectionnés qui ne correspondent plus au rôle
                  if (newRoles.length) {
                    setSelProfiles(prev =>
                      prev.filter(id => metaProfiles.find(p => p.id === id && newRoles.includes(p.role)))
                    );
                  }
                }}
                placeholder="Tous les rôles"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Action</label>
              <MultiSelect
                options={ACTION_OPTIONS}
                value={selActions}
                onChange={setSelActions}
                placeholder="Toutes les actions"
              />
            </div>
          </div>

          {/* Ligne 2 : dates + passage */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">ID passage (musiqID)</label>
              <input type="text" placeholder="ex: OXTB1R1Z8U" value={passageUid}
                onChange={e=>setPassageUid(e.target.value)} className={INPUT} />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Du</label>
              <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)} className={INPUT} />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Au</label>
              <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)} className={INPUT} />
            </div>
          </div>

          {hasFilters && (
            <button onClick={reset} className="text-xs text-red-600 hover:underline">
              Réinitialiser tous les filtres
            </button>
          )}
        </div>

        {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-2 text-sm">{error}</div>}

        {/* ── Tableau ── */}
        <div className="bg-white rounded-xl border overflow-hidden">
          {loading && <div className="flex items-center justify-center py-12 text-slate-400 text-sm animate-pulse">Chargement…</div>}
          {!loading && !rows.length && <div className="flex items-center justify-center py-12 text-slate-400 text-sm">Aucune entrée trouvée.</div>}
          {!loading && rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b text-xs text-slate-500 uppercase tracking-wide text-left">
                    <th className="px-4 py-3 whitespace-nowrap">Horodatage</th>
                    <th className="px-4 py-3">Profil</th>
                    <th className="px-4 py-3">Action</th>
                    <th className="px-4 py-3">Patient</th>
                    <th className="px-4 py-3">Site</th>
                    <th className="px-4 py-3">Détails</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map(row => (
                    <tr key={row.id}
                      onClick={()=>setExpanded(expanded===row.id?null:row.id)}
                      className={`hover:bg-slate-50 cursor-pointer transition-colors ${expanded===row.id?'bg-slate-50':''}`}>
                      <td className="px-4 py-3 whitespace-nowrap font-mono text-xs text-slate-500">{formatTs(row.ts)}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-800">{row.profile_display||<span className="text-slate-400 italic text-xs">—</span>}</div>
                        {row.user_role && <RoleBadge role={row.user_role} />}
                      </td>
                      <td className="px-4 py-3"><Badge action={row.action} /></td>
                      <td className="px-4 py-3">
                        {row.passage_uid ? (
                          <div>
                            <div className="font-mono text-xs text-slate-400">{row.passage_uid}</div>
                            {(row.passage_prenom||row.passage_nom) &&
                              <div className="text-sm text-slate-700">{[row.passage_prenom,row.passage_nom].filter(Boolean).join(' ')}</div>}
                          </div>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">
                        {row.etablissement_libelle||<span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-4 py-3 max-w-xs">
                        <DetailCell details={row.details} action={row.action} />
                        {expanded===row.id && (
                          <pre className="mt-2 text-xs bg-slate-100 rounded p-2 overflow-auto max-h-36 whitespace-pre-wrap text-slate-600">
                            {JSON.stringify(row.details,null,2)}
                          </pre>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {total > LIMIT && (
          <div className="flex items-center justify-between text-sm text-slate-600">
            <span>{offset+1}–{Math.min(offset+LIMIT,total)} sur {total.toLocaleString('fr-FR')}</span>
            <div className="flex gap-2">
              <button onClick={()=>fetchAudit(Math.max(0,offset-LIMIT))} disabled={offset===0}
                className="px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50 disabled:opacity-40">← Précédent</button>
              <button onClick={()=>fetchAudit(offset+LIMIT)} disabled={offset+LIMIT>=total}
                className="px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50 disabled:opacity-40">Suivant →</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
